<template>
  <div v-if="visible" class="game-over-overlay" @click="skipAll">
    <div
      class="game-over-panel"
      :class="headline ? 'watch' : winner === 'player' ? 'victory' : 'defeat'"
    >
      <div class="game-over-busts">
        <AvatarView
          :config="leftAvatar"
          crop="bust"
          circle
          :size="bustSize"
        />
        <span class="game-over-vs">VS</span>
        <AvatarView
          :config="rightAvatar"
          crop="bust"
          circle
          :size="bustSize"
        />
      </div>
      <div class="game-over-title">
        {{ headline || (winner === 'player' ? t('duel.victory') : t('duel.defeat')) }}
      </div>
      <div class="game-over-subtitle">{{ howLine }}</div>
      <div v-if="rewardDelta > 0" class="game-over-reward">
        {{ t('duel.reward', { n: rewardDelta }) }}
      </div>
      <p v-if="nudgeCoins > 0" class="game-over-nudge">
        {{ t('duel.rankedNudgePaid', { n: nudgeCoins }) }}
      </p>
      <p v-if="rankedOfferCoins > 0" class="game-over-offer">
        {{ t('duel.rankedOffer', { n: rankedOfferCoins }) }}
      </p>
      <XpLevelBar
        v-if="showXp && progress"
        ref="xpBar"
        :progress="progress"
        :play="visible"
        @done="onBarDone"
      />
      <div v-if="levelUpLine && actionsReady" class="game-over-level">
        <p class="game-over-level-title">{{ levelUpLine }}</p>
        <p v-if="grantLine" class="game-over-level-grant">{{ grantLine }}</p>
      </div>
      <p v-if="showBanlistPick && actionsReady && !tournament" class="game-over-level-grant">
        {{ t('banlistReward.gameOver') }}
      </p>
      <p v-if="tournament" class="game-over-tournament">
        {{ t('duel.tournamentPrizes') }}
      </p>
      <RankLpBar
        v-if="ranked"
        ref="rankBar"
        :ranked="ranked"
        :play="visible"
        :badge-size="badgeSize"
        @done="onBarDone"
      />
      <LeagueCosmeticsNotice
        v-if="ranked?.cosmeticsUnlocked && actionsReady"
        :league="ranked.cosmeticsUnlocked"
      />
      <div class="game-over-actions" :class="{ ready: actionsReady }">
        <button
          v-if="rankedOfferCoins > 0 && !tournament"
          type="button"
          class="game-over-btn primary"
          @click.stop="$emit('play-ranked')"
        >
          {{ t('duel.rankedOfferGo') }}
        </button>
        <button
          v-if="showBanlistPick && !tournament"
          type="button"
          class="game-over-btn"
          :class="rankedOfferCoins > 0 ? 'secondary' : 'primary'"
          @click.stop="onChooseBanlist"
        >
          {{ t('banlistReward.gameOverChoose') }}
        </button>
        <button
          v-if="!hideReplay && !tournament"
          type="button"
          class="game-over-btn"
          :class="rankedOfferCoins > 0 || showBanlistPick ? 'secondary' : 'primary'"
          @click="$emit('play-again')"
        >
          {{ t('duel.playAgain') }}
        </button>
        <button
          type="button"
          class="game-over-btn"
          :class="tournament ? 'primary' : 'secondary'"
          @click="$emit('return-to-lobby')"
        >
          {{ leaveLabel || (hideReplay ? t('duel.watchBack') : t('duel.returnLobby')) }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import { useCompactLayout } from '../composables/useCompactLayout'
import { useI18n } from '../i18n/t'
import {
  DEFAULT_AVATAR,
  type AvatarConfig,
  type DuelXpResult,
  type RankedDelta,
} from '@shared/index'
import AvatarView from './AvatarView.vue'
import RankLpBar from './RankLpBar.vue'
import LeagueCosmeticsNotice from './LeagueCosmeticsNotice.vue'
import XpLevelBar from './XpLevelBar.vue'
import { useBanlistRewardStore } from '../stores/banlistReward'

const props = withDefaults(
  defineProps<{
    visible: boolean
    winner: 'player' | 'opponent' | null
    winReason?: string | null
    leftAvatar?: AvatarConfig
    rightAvatar?: AvatarConfig
    rewardDelta?: number
    ranked?: RankedDelta | null
    progress?: DuelXpResult | null
    /** Coins already inside rewardDelta, paid for playing this Quick Match. */
    nudgeCoins?: number
    /** This vs-AI duel unlocked a free coin offer for one Quick Match. */
    rankedOfferCoins?: number
    /** Admin watch: who won, instead of victory / defeat. */
    headline?: string
    detail?: string
    hideReplay?: boolean
    /** Tournament match: prizes wait in the mailbox, so there is no rematch. */
    tournament?: boolean
    /** Spectator leave copy. Empty keeps the default watch / lobby label. */
    leaveLabel?: string
  }>(),
  {
    leftAvatar: () => ({ ...DEFAULT_AVATAR }),
    rightAvatar: () => ({ ...DEFAULT_AVATAR }),
    rewardDelta: 0,
    ranked: null,
    progress: null,
    nudgeCoins: 0,
    rankedOfferCoins: 0,
    winReason: null,
    headline: '',
    detail: '',
    hideReplay: false,
    tournament: false,
    leaveLabel: '',
  },
)

defineEmits<{
  'return-to-lobby': []
  'play-again': []
  'play-ranked': []
}>()

const { t } = useI18n()
const banlistReward = useBanlistRewardStore()
const { menusCompact } = useCompactLayout()
const bustSize = computed(() => (menusCompact.value ? 52 : 120))
const badgeSize = computed(() => (menusCompact.value ? 44 : 72))

const howLine = computed(() => {
  if (props.detail) return props.detail
  if (props.headline) return ''
  const win = props.winner === 'player'
  const reason = props.winReason
  if (reason === 'life-points') {
    return t(win ? 'duel.overBy.lifePointsWin' : 'duel.overBy.lifePointsLose')
  }
  if (reason === 'deck-out') {
    return t(win ? 'duel.overBy.deckOutWin' : 'duel.overBy.deckOutLose')
  }
  if (reason === 'exodia') {
    return t(win ? 'duel.overBy.exodiaWin' : 'duel.overBy.exodiaLose')
  }
  if (reason === 'surrender') {
    return t(win ? 'duel.overBy.surrenderWin' : 'duel.overBy.surrenderLose')
  }
  if (reason === 'forfeit') {
    return t(win ? 'duel.overBy.forfeitWin' : 'duel.overBy.forfeitLose')
  }
  if (reason === 'timeout') {
    return t(win ? 'duel.overBy.timeoutWin' : 'duel.overBy.timeoutLose')
  }
  if (reason === 'round-time') {
    return t(win ? 'duel.overBy.roundTimeWin' : 'duel.overBy.roundTimeLose')
  }
  return win ? t('duel.victorySub') : t('duel.defeatSub')
})

const showXp = computed(() => (props.progress?.xpDelta ?? 0) > 0)
const banlistPickReady = computed(() => (props.progress?.banlistPicks ?? 0) > 0)
const showBanlistPick = computed(
  () => banlistPickReady.value && banlistReward.available > 0,
)

function onChooseBanlist(): void {
  const reward = props.progress?.banlistReward
  if (
    reward &&
    reward.claimed >= banlistReward.claimed &&
    reward.unlocked >= banlistReward.unlocked
  ) {
    banlistReward.apply(reward, true)
  }
  banlistReward.openDialog()
}

const levelUpLine = computed(() => {
  const grants = props.progress?.grants ?? []
  if (grants.length === 0 || !props.progress) return ''
  return t('level.levelUp', { n: props.progress.level })
})

const grantLine = computed(() => {
  const grants = props.progress?.grants ?? []
  if (grants.length === 0) return ''
  const coins = grants.reduce((sum, g) => sum + g.coins, 0)
  const packs = grants.reduce((sum, g) => sum + g.packs, 0)
  if (packs > 0 && coins > 0) return t('level.grantBoth', { n: coins })
  if (packs > 0) return t('level.grantPack')
  if (coins > 0) return t('level.grantCoins', { n: coins })
  return ''
})

/** The action buttons wait for the progress bars so the fill is readable. */
const ACTIONS_SAFETY_MS = 2500

const actionsReady = ref(false)
const rankBar = ref<InstanceType<typeof RankLpBar> | null>(null)
const xpBar = ref<InstanceType<typeof XpLevelBar> | null>(null)
let pendingBars = 0
let safetyTimer = 0

function releaseActions(): void {
  pendingBars = 0
  actionsReady.value = true
  window.clearTimeout(safetyTimer)
  safetyTimer = 0
}

function onBarDone(): void {
  pendingBars = Math.max(0, pendingBars - 1)
  if (pendingBars === 0) releaseActions()
}

function skipAll(): void {
  rankBar.value?.skip()
  xpBar.value?.skip()
  releaseActions()
}

function onKey(): void {
  if (props.visible) skipAll()
}

watch(
  () => props.visible,
  (visible) => {
    window.clearTimeout(safetyTimer)
    safetyTimer = 0
    if (!visible) {
      window.removeEventListener('keydown', onKey)
      actionsReady.value = false
      pendingBars = 0
      return
    }
    window.addEventListener('keydown', onKey)
    pendingBars = (showXp.value ? 1 : 0) + (props.ranked ? 1 : 0)
    actionsReady.value = pendingBars === 0
    if (pendingBars > 0) {
      safetyTimer = window.setTimeout(releaseActions, ACTIONS_SAFETY_MS)
    }
  },
  { immediate: true },
)

onBeforeUnmount(() => {
  window.clearTimeout(safetyTimer)
  window.removeEventListener('keydown', onKey)
})
</script>

<style scoped>
.game-over-overlay {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  background: rgba(0, 0, 0, 0.85);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: fadeIn 0.4s ease-out;
}

.game-over-panel {
  background: #0d0d1a;
  border: 3px solid;
  border-radius: 14px;
  padding: 48px 64px;
  text-align: center;
  box-shadow: 0 8px 60px rgba(0, 0, 0, 0.9);
  min-width: 480px;
  animation: popIn 0.6s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.game-over-panel.victory {
  border-color: #facc15;
  box-shadow: 0 0 60px rgba(250, 204, 21, 0.45), 0 8px 40px rgba(0, 0, 0, 0.85);
}

.game-over-panel.defeat {
  border-color: #ef4444;
  box-shadow: 0 0 60px rgba(239, 68, 68, 0.4), 0 8px 40px rgba(0, 0, 0, 0.85);
}

.game-over-busts {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 20px;
  margin-bottom: 20px;
}

.game-over-vs {
  font-size: 28px;
  font-weight: 900;
  letter-spacing: 0.08em;
  color: #e8c547;
}

.game-over-title {
  font-size: 64px;
  font-weight: 900;
  letter-spacing: 6px;
  margin-bottom: 14px;
  text-shadow: 0 2px 12px rgba(0, 0, 0, 0.7);
}

.game-over-panel.watch {
  border-color: #facc15;
  box-shadow: 0 0 60px rgba(250, 204, 21, 0.45), 0 8px 40px rgba(0, 0, 0, 0.85);
}

.watch .game-over-title {
  color: #facc15;
  font-size: 36px;
  letter-spacing: 1px;
}

.victory .game-over-title {
  color: #facc15;
}

.defeat .game-over-title {
  color: #ef4444;
}

.game-over-subtitle {
  color: #e5e7eb;
  font-size: 18px;
  margin-bottom: 16px;
  opacity: 0.9;
}

.game-over-reward {
  color: #facc15;
  font-size: 20px;
  font-weight: 800;
  letter-spacing: 0.04em;
  margin-bottom: 8px;
}

.game-over-nudge,
.game-over-offer,
.game-over-tournament {
  margin: 0 0 12px;
  color: #fde68a;
  font-size: 16px;
  font-weight: 700;
  line-height: 1.35;
}

.game-over-tournament {
  max-width: 420px;
  margin-left: auto;
  margin-right: auto;
}

.game-over-level {
  margin-bottom: 16px;
  animation: fadeIn var(--md-motion-medium, 300ms)
    var(--md-motion-standard, cubic-bezier(0.2, 0, 0, 1));
}

.game-over-level-title {
  margin: 0;
  color: #facc15;
  font-size: 18px;
  font-weight: 800;
}

.game-over-level-grant {
  margin: 4px 0 0;
  color: #fde68a;
  font-size: 14px;
  font-weight: 600;
}

.game-over-actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 14px;
  opacity: 0;
  transform: translateY(8px);
  pointer-events: none;
  transition:
    opacity var(--md-motion-medium, 300ms)
      var(--md-motion-standard, cubic-bezier(0.2, 0, 0, 1)),
    transform var(--md-motion-medium, 300ms)
      var(--md-motion-standard, cubic-bezier(0.2, 0, 0, 1));
}

.game-over-actions.ready {
  opacity: 1;
  transform: translateY(0);
  pointer-events: auto;
}

.game-over-btn {
  padding: 12px 28px;
  border-radius: 6px;
  border: none;
  font-size: 15px;
  font-weight: 700;
  letter-spacing: 1px;
  cursor: pointer;
  transition: transform 0.15s, box-shadow 0.15s;
  color: #0d0d1a;
}

.game-over-btn.primary {
  background: #facc15;
}

.game-over-btn.primary:hover {
  transform: translateY(-1px);
  box-shadow: 0 6px 20px rgba(250, 204, 21, 0.45);
}

.game-over-btn.secondary {
  background: #1f2937;
  color: #e5e7eb;
  border: 1px solid #4b5563;
}

.game-over-btn.secondary:hover {
  transform: translateY(-1px);
  background: #2d3748;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.5);
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes popIn {
  0% { transform: scale(0.5); opacity: 0; }
  100% { transform: scale(1); opacity: 1; }
}

@media (max-width: 720px), (max-height: 500px) and (max-width: 1100px) {
  .game-over-overlay {
    box-sizing: border-box;
    align-items: center;
    padding:
      max(16px, env(safe-area-inset-top, 0px))
      max(16px, env(safe-area-inset-right, 0px))
      max(16px, env(safe-area-inset-bottom, 0px))
      max(16px, env(safe-area-inset-left, 0px));
  }

  .game-over-panel {
    box-sizing: border-box;
    min-width: 0;
    width: min(420px, 100%);
    max-height: calc(100vh - 32px);
    max-height: calc(
      100dvh
      - max(16px, env(safe-area-inset-top, 0px))
      - max(16px, env(safe-area-inset-bottom, 0px))
    );
    min-height: 0;
    overscroll-behavior: contain;
    overflow-x: hidden;
    overflow-y: auto;
    padding: 12px 16px 10px;
    border-width: 2px;
    border-radius: 12px;
    animation: popInFit 0.35s var(--md-motion-standard, cubic-bezier(0.2, 0, 0, 1));
  }

  .game-over-panel.victory {
    box-shadow: 0 0 22px rgba(250, 204, 21, 0.35), 0 6px 24px rgba(0, 0, 0, 0.8);
  }

  .game-over-panel.defeat {
    box-shadow: 0 0 22px rgba(239, 68, 68, 0.32), 0 6px 24px rgba(0, 0, 0, 0.8);
  }

  .game-over-busts {
    gap: 12px;
    margin-bottom: 6px;
  }

  .game-over-vs {
    font-size: 16px;
  }

  .game-over-title {
    font-size: 28px;
    letter-spacing: 2px;
    line-height: 1.05;
    margin-bottom: 4px;
  }

  .game-over-subtitle {
    font-size: 14px;
    line-height: 1.25;
    margin-bottom: 6px;
  }

  .game-over-reward {
    font-size: 15px;
    margin-bottom: 4px;
  }

  .game-over-nudge,
  .game-over-offer,
  .game-over-tournament {
    font-size: 13px;
    margin-bottom: 6px;
  }

  .game-over-level {
    margin-bottom: 6px;
  }

  .game-over-level-title {
    font-size: 15px;
  }

  .game-over-level-grant {
    font-size: 13px;
  }

  .game-over-panel :deep(.xp-bar) {
    gap: 2px;
    margin-bottom: 6px;
  }

  .game-over-panel :deep(.xp-bar-chip),
  .game-over-panel :deep(.xp-bar-delta),
  .game-over-panel :deep(.xp-bar-text) {
    font-size: 12px;
  }

  .game-over-panel :deep(.xp-track),
  .game-over-panel :deep(.rank-track) {
    width: min(220px, 100%);
    height: 8px;
  }

  .game-over-panel :deep(.rank-bar) {
    gap: 4px;
    margin-bottom: 8px;
  }

  .game-over-panel :deep(.rank-bar-name),
  .game-over-panel :deep(.rank-bar-delta),
  .game-over-panel :deep(.rank-bar-text),
  .game-over-panel :deep(.rank-bar-shift) {
    font-size: 13px;
  }

  .game-over-actions {
    gap: 8px;
    flex-wrap: wrap;
  }

  .game-over-btn {
    padding: 8px 14px;
    font-size: 13px;
  }
}

@keyframes popInFit {
  0% { transform: scale(0.94); opacity: 0; }
  100% { transform: scale(1); opacity: 1; }
}

@media (prefers-reduced-motion: reduce) {
  .game-over-overlay,
  .game-over-panel,
  .game-over-level {
    animation: none;
  }

  .game-over-actions {
    transition: none;
  }
}
</style>
